import { Box } from "@chakra-ui/react";
import Decimal from "decimal.js";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { DraftReallocationAlert } from "features/capital-activity/DraftReallocationAlert";
import { CapitalEventStatsCard } from "features/capital-activity/event/CapitalEventStatsCard";
import { useDraftReallocationState } from "features/capital-activity/event/useHasDraftReallocation";
import { CapitalCallBreakdownTable } from "features/capital-call/CapitalCallBreakdownPage";
import { CapitalCallDrawdownTable } from "features/capital-call/drawdown/CapitalCallDrawdownTable";
import { useCapitalCallFeeBasisNotificationTableDataRetrieveSuspense } from "gen/endpoints/capital-call/capital-call";
import {
  useCreditFacilityListSuspense,
  useCreditFacilitySummaryRetrieve,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  PageWizardProvider,
  PageWizardRoutes,
} from "shared/components/layout/PageWizard";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute, RouteId } from "shared/navigation/Routes";

export const capitalCallReallocationCreateRoute = ({
  fundFamilyId,
  fundFamilyEventId,
  reallocationFundFamilyEventId,
}: {
  fundFamilyId: string;
  fundFamilyEventId: string;
  reallocationFundFamilyEventId: string;
}): string =>
  rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId })
    .calls({ fundFamilyId, fundFamilyEventId })
    .capitalActivityCapitalActivityCallReallocation({
      reallocationFundFamilyEventId,
    })
    .capitalActivityCapitalActivityEventReallocationUpdate({}).$;

export const capitalCallReallocationReviewRoute = ({
  fundFamilyId,
  fundFamilyEventId,
  reallocationFundFamilyEventId,
}: {
  fundFamilyId: string;
  fundFamilyEventId: string;
  reallocationFundFamilyEventId: string;
}): string =>
  rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId })
    .calls({ fundFamilyId, fundFamilyEventId })
    .capitalActivityCapitalActivityCallReallocation({
      reallocationFundFamilyEventId,
    })
    .capitalActivityCapitalCallReallocationOverview({}).$;

export const CapitalCallPage: React.FC = () => {
  const { t } = useTranslation(["common", "capitalCall"]);
  const navigate = useNavigate();

  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  // reallocation event id will only be used if the user navigates to the reallocation page so can't check if required
  const { reallocationFundFamilyEventId } = useParams();
  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data: feeBasisData } =
    useCapitalCallFeeBasisNotificationTableDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );
  const isCfReallocViaCapitalCallActive = useFeatureFlagActive(
    "credit_facility_reallocation_via_capital_calls",
  );

  // hide the fee basis notification page if either the feature flag is not active or if no fee basis overlap exists
  // we also hide the reallocation page always as we want it as a child of this route but it has it's own page wizard and this prevents nesting page wizard issues
  const hiddenPages: Partial<Record<RouteId, boolean>> = {
    capitalActivityCapitalCallEventOverview: true,
    capitalActivityCapitalActivityCallReallocation: true,
    feeBasisNotification:
      !isCfReallocViaCapitalCallActive ||
      Object.keys(
        feeBasisData.capital_call_credit_facility_overlap_data
          .investment_id_to_cf_amount,
      ).length === 0,
  };

  const { data: creditFacilityList } =
    useCreditFacilityListSuspense(fundFamilyId);
  const creditFacilityId = creditFacilityList[0]?.id;
  const { data: creditFacilitySummaryData } = useCreditFacilitySummaryRetrieve(
    fundFamilyId,
    creditFacilityId,
    {
      fund_family_event_id: fundFamilyEventId,
    },
    {
      query: {
        enabled: !!creditFacilityId,
      },
    },
  );

  const { borrowing_base_amount, has_borrowing_base_groups } =
    creditFacilitySummaryData ?? {};

  const showBorrowingBaseTable =
    new Decimal(borrowing_base_amount ?? "0.00").greaterThan(0) &&
    !!has_borrowing_base_groups;

  const activityEventPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId }).$;

  const isDraft =
    eventData.state == FundFamilyEventStateMachineFieldsEnum.draft;

  const { hasDraftAllocation } = useDraftReallocationState({
    fundFamilyId,
    fundFamilyEventId,
  });

  if (!isDraft && !reallocationFundFamilyEventId) {
    return (
      <PageContent isFullHeight={true}>
        <PageHeader
          title={t("capitalCall:capitalCall")}
          badge={getCapitalActivityEventStateBadge(eventData.state, t, false)}
          backButton={{
            title: t("common:capitalActivity"),
            path: activityEventPath,
          }}
          stats={
            <CapitalEventStatsCard
              capitalActivityData={eventData}
              isEqualizedView={isEqualizedView}
              setIsEqualized={setIsEqualizedView}
              isDraftEvent={isDraft}
              showReallocation={!hasDraftAllocation}
            />
          }
        />
        <PageBody>
          <DraftReallocationAlert
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            reallocationFundFamilyEventId={reallocationFundFamilyEventId}
          />
          <Box mb="24px">
            <CapitalCallDrawdownTable
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
              isEqualizedView={isEqualizedView}
            />
            <CapitalCallBreakdownTable
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
              creditFacilityId={creditFacilityId}
              showBorrowingBaseTable={showBorrowingBaseTable}
              isEqualizedView={isEqualizedView}
            />
          </Box>
        </PageBody>
      </PageContent>
    );
  }

  const navigateToActivityEvent = () => {
    navigate(activityEventPath);
  };

  return (
    <PageWizardProvider
      finishTitle={t("capitalCall:finalizeCall")}
      onFinish={navigateToActivityEvent}
      onExit={navigateToActivityEvent}
      hiddenPages={hiddenPages}
    >
      <PageContent isFullHeight={true}>
        <PageWizardRoutes />
      </PageContent>
    </PageWizardProvider>
  );
};
